import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { RESOURCES, CONTACT } from "../data/site";
import { ArrowUpRight, BookOpen, GraduationCap } from "lucide-react";

const Card = ({ item, kind, i }) => (
  <Reveal delay={i * 0.08}>
    <a href={item.url} target="_blank" rel="noreferrer" data-testid={`resource-${kind}-${i}`}
      className="group flex flex-col md:flex-row gap-6 bg-white border border-gold/25 hover:border-teal p-6 md:p-8 transition-all duration-500 h-full">
      <div className="w-full md:w-48 shrink-0 aspect-[3/4] overflow-hidden bg-cream flex items-center justify-center self-start">
        <img src={item.cover} alt={item.title} className={`w-full h-full ${kind === "book" ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-[900ms]`}
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div className="flex flex-col justify-between grow">
        <div>
          <p className="uppercase tracking-[0.25em] text-xs text-teal mb-2 flex items-center gap-2">
            {kind === "book" ? <><BookOpen size={13} /> Book</> : <><GraduationCap size={13} /> Course · {item.provider}</>}
          </p>
          <h3 className="font-serif text-2xl md:text-3xl text-navy leading-tight mb-2">{item.title}</h3>
          {item.author && <p className="font-serif italic text-lg text-teal mb-3">by {item.author}</p>}
          <p className="text-charcoal/75 leading-relaxed mb-5">{item.description}</p>
        </div>
        <span className="inline-flex items-center gap-2 text-navy font-medium text-sm">
          {item.cta} <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform duration-300" />
        </span>
      </div>
    </a>
  </Reveal>
);

export default function Resources() {
  return (
    <div data-testid="resources-page" className="pt-32">
      <SEO
        title="Books & Courses by Tanya Richardson, LMHC | Beyond Blessed"
        description="Faith-based devotionals and clinical courses from Tanya Richardson, LMHC — God's Diamonds, All the King's Daughters, The Marriage Purpose & more."
        path="/resources"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Books & Courses</p>
          <h1 className="font-serif text-5xl md:text-7xl text-navy leading-none">Take healing<br /><span className="italic text-teal">home with you.</span></h1>
          <p className="text-charcoal/70 max-w-xl mt-6">Faith-based reads and clinical courses from Tanya and trusted educators—for the seasons between sessions.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="resources-books">
        <Reveal>
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif text-3xl md:text-4xl text-navy">Devotional Reads</h2>
            <p className="text-charcoal/50 text-sm italic">Available on Amazon</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {RESOURCES.books.map((b, i) => <Card key={b.title} item={b} kind="book" i={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="resources-courses">
        <Reveal>
          <div className="flex items-baseline justify-between mb-8 mt-8">
            <h2 className="font-serif text-3xl md:text-4xl text-navy">Learn With Us</h2>
            <p className="text-charcoal/50 text-sm italic">Online courses</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {RESOURCES.courses.map((c, i) => <Card key={c.title} item={c} kind="course" i={i} />)}
        </div>
      </section>

      <section className="bg-teal text-cream py-20 text-center" data-testid="resources-cta">
        <div className="max-w-3xl mx-auto px-6">
          <p className="font-script text-3xl text-goldbright mb-2">{CONTACT.consultCopy}</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">Ready to go deeper than a book?</h2>
          <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid="resources-consult" className="bg-cream text-navy px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-goldbright transition-colors">
            Book a free 15-min consultation <ArrowUpRight size={17} />
          </a>
        </div>
      </section>
    </div>
  );
}
